import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Spin, Table, Input, Tag } from "antd";
import { useSelector } from "react-redux";
import {
  getDomainsPaginated,
  deleteDomainById,
  updateDomainStatus,
} from "@api/email/users/domain";
import { EmailNotificationModal } from "@components";

const { Search } = Input;

const EmailDomainList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);

  // State variables
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalDomains: 0,
  });

  // Notification modal states
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [selectedDomainName, setSelectedDomainName] = useState("");

  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  // Fetch domains on component mount and when pagination/search changes
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, totalDomains: 0 });
    fetchDomains(page, pageSize, search);
  }, []);

  // Modal handling functions
  const showSuccessModal = (message) => {
    setSuccessMessage(message);
    setSuccessModalVisible(true);
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  const showInfoModal = (message) => {
    setInfoMessage(message);
    setInfoModalVisible(true);
  };

  // Function to fetch domains
  const fetchDomains = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getDomainsPaginated(page, pageSize, searchText);
      setDomains(res.data.domains || []);
      setPagination((p) => ({
        ...p,
        totalDomains: res.data.paginationData?.totalDomains,
        current: res.data.paginationData?.page,
        pageSize: res.data.paginationData?.perPage,
      }));
    } catch (err) {
      console.error("Fetch domains error", err);
      showErrorModal("Failed to fetch domains. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    fetchDomains(page, pageSize, searchText);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 });
    fetchDomains(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle domain update
  const handleDomainUpdate = (id) => {
    history.push(`/super-admin/email/domain/${id}/update`);
  };

  // Function to prepare domain delete
  const handleDomainDelete = (id, domainName) => {
    setSelectedDomainId(id);
    setSelectedDomainName(domainName);
    setDeleteModalVisible(true);
  };

  // Function to confirm domain deletion
  const confirmDomainDelete = async () => {
    try {
      await deleteDomainById(selectedDomainId);
      showSuccessModal(
        `Domain "${selectedDomainName}" has been deleted successfully`
      );

      // Refresh the domain list after a short delay
      setTimeout(() => {
        fetchDomains(pagination.current, pagination.pageSize, searchText);
      }, 1000);
    } catch (error) {
      console.error("Error deleting domain", error);
      showErrorModal(
        error.response?.data?.message ||
          "Failed to delete domain. Please try again."
      );
    }
  };

  // Function to handle status update
  const handleStatusUpdate = async (id, status, domainName) => {
    try {
      await updateDomainStatus(id, status);

      const statusMessage =
        status === "active"
          ? `Domain "${domainName}" has been activated successfully`
          : `Domain "${domainName}" has been deactivated successfully`;

      showSuccessModal(statusMessage);

      // Refresh the domain list after a short delay
      setTimeout(() => {
        fetchDomains(pagination.current, pagination.pageSize, searchText);
      }, 1000);
    } catch (error) {
      console.error("Error updating domain status", error);
      showErrorModal(
        error.response?.data?.message ||
          "Failed to update domain status. Please try again."
      );
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Domain Name",
      dataIndex: "domainName",
      key: "domainName",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Created By",
      key: "createdBy",
      render: (_, record) => record.createdBy?.name || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() => handleDomainUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type={record.status === "active" ? "default" : "primary"}
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() =>
              handleStatusUpdate(
                record._id,
                record.status === "active" ? "inactive" : "active",
                record.domainName
              )
            }
          >
            {record.status === "active" ? "Deactivate" : "Activate"}
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleDomainDelete(record._id, record.domainName)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Card style={{ borderRadius: "0", height: "100vh", overflowY: "hidden" }}>
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <h3>Domain List</h3>
          </Col>
          <Col>
            {/* Create Domain Button */}
            <Button
              type="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push("/super-admin/email/domain/create")}
            >
              Add Domain
            </Button>

            {/* View SMTP Servers */}
            <Button
              type="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push("/super-admin/email/smtp")}
            >
              View SMTP Servers
            </Button>

            {/* View User Emails */}
            <Button
              type="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push("/super-admin/email/users")}
            >
              View User Emails
            </Button>

            {/* Search */}
            <Search
              placeholder="Search by domain name/description"
              style={{ width: "300px", marginRight: 10 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
            />
          </Col>
        </Row>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={domains}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.totalDomains,
            }}
            loading={loading}
            onChange={({ current, pageSize }) =>
              handlePaginationChange(current, pageSize)
            }
            rowKey={(record) => record._id}
            scroll={{ y: "calc(100vh - 240px)" }}
            tableLayout="fixed"
          />
        </Spin>
      </Card>

      {/* Delete Confirmation Modal */}
      <EmailNotificationModal
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        type="delete"
        title="Delete Domain"
        message={`Are you sure you want to delete the domain "${selectedDomainName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDomainDelete}
      />

      {/* Success Notification Modal */}
      <EmailNotificationModal
        isOpen={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        type="success"
        title="Success"
        message={successMessage}
        autoCloseTime={3000}
      />

      {/* Error Notification Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Error"
        message={errorMessage}
      />

      {/* Info Notification Modal */}
      <EmailNotificationModal
        isOpen={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        type="info"
        title="Information"
        message={infoMessage}
      />
    </>
  );
};

export default EmailDomainList;
